import i18n from '../../i18n';
import { BaseApiClient } from './base-client';
import type {
  FpsOption,
  TrimRange,
  ColorScheme,
  ActiveRestyleGeneration,
  VideoRestyleHistoryResponse,
} from '../../pages/VideoRestyle/types';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function sanitizeTeamId(teamId?: string | null): string | null {
  return teamId && UUID_REGEX.test(teamId) ? teamId : null;
}

/**
 * Video Restyle API Response Types
 */
interface VideoRestyleAttachment {
  id: string;
  userId: string;
  s3Key: string;
  s3Url: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  duration: number;
  createdAt: string;
}

interface VideoRestyleGenerateResponse {
  id: string;
  jobId?: string;
  status: 'queued' | 'processing';
  tokensUsed: number;
  frameCount: number;
  fps: number;
  duration: number;
  prompt: string;
  originalPrompt?: string;
  wasTranslated?: boolean;
}

interface VideoRestylePollResponse {
  id: string;
  status: 'queued' | 'generating' | 'completed' | 'error';
  progress?: number;
  processedFrames?: number;
  totalFrames?: number;
  currentFramePreview?: string;
  video?: {
    id?: string;
    url: string;
    duration?: number;
    thumbnailUrl?: string;
  };
  error?: string;
  tokensRefunded?: boolean;
}

interface GenerationParams {
  video_url: string;
  prompt: string;
  fps: FpsOption;
  isPro: boolean;
  proResolution?: '1K' | '2K';
  colorScheme: ColorScheme;
  trimRange: TrimRange;
  enhancePrompt?: boolean;
  autoTranslate?: boolean;
  teamId?: string;
  folderId?: string;
}

interface PreviewParams {
  video_url: string;
  prompt: string;
  isPro: boolean;
  proResolution?: '1K' | '2K';
  colorScheme: ColorScheme;
  frameTime: number; // time in seconds for the frame to extract
  enhancePrompt?: boolean;
  teamId?: string;
  folderId?: string;
}

interface PreviewResponse {
  id: string;
  url: string;
  thumbnailUrl?: string;
  tokensUsed: number;
  prompt: string;
}

/**
 * Video Restyle API - methods for AI video restyling (Нейро-стопмоушн)
 * Frame-by-frame stylization using Nano Banana Pro or Seedream 4.5
 */
export class VideoRestyleApi extends BaseApiClient {

  // ===== ATTACHMENTS API =====

  /**
   * Get saved video attachment for user
   */
  async getAttachment(): Promise<{ video: VideoRestyleAttachment | null }> {
    try {
      const response = await this.client.get('/api/video/restyle/attachments');
      return response.data.data;
    } catch (error: unknown) {
      console.error('Error fetching video restyle attachment:', error);
      return { video: null };
    }
  }

  /**
   * Upload a video for restyle
   * @param file - Video file to upload
   * @param duration - Video duration in seconds
   * @param onProgress - Upload progress callback
   */
  async uploadVideo(
    file: File,
    duration: number,
    onProgress?: (progress: number) => void
  ): Promise<{ attachment: VideoRestyleAttachment }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('duration', duration.toString());

    const response = await this.client.post('/api/video/restyle/attachments', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data.data;
  }

  /**
   * Delete a video attachment
   */
  async deleteAttachment(attachmentId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/api/video/restyle/attachments/${attachmentId}`);
    return response.data;
  }

  /**
   * Clear all attachments
   */
  async clearAttachments(): Promise<{ message: string }> {
    const response = await this.client.delete('/api/video/restyle/attachments/clear/all');
    return response.data;
  }

  // ===== PREVIEW API =====

  /**
   * Generate a preview image (single styled frame)
   * 
   * @param params - Preview parameters
   * @returns Preview image URL and metadata
   */
  async generatePreview(params: PreviewParams): Promise<PreviewResponse> {
    try {
      const teamId = sanitizeTeamId(params.teamId);
      const response = await this.client.post('/api/video/restyle/preview', { ...params, teamId });
      return response.data.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string; insufficientBalance?: boolean; tokensRequired?: number }; status?: number }; message?: string };
      const errorData = err.response?.data;
      const errorMessage = typeof errorData?.error === 'string'
        ? errorData.error
        : err.message || i18n.t('common:error.previewGeneration');

      const normalizedError = {
        message: errorMessage,
        response: {
          status: err.response?.status || 500,
          data: {
            error: errorMessage,
            insufficientBalance: errorData?.insufficientBalance,
            tokensRequired: errorData?.tokensRequired
          }
        }
      };
      throw normalizedError;
    }
  }

  // ===== GENERATION API =====

  /**
   * Start video restyle generation
   * 
   * @param params - Generation parameters
   * @returns Generation ID and initial status
   */
  async generate(params: GenerationParams): Promise<VideoRestyleGenerateResponse> {
    try {
      const teamId = sanitizeTeamId(params.teamId);
      const response = await this.client.post('/api/video/restyle/generate', { ...params, teamId });
      return response.data.data;
    } catch (error: unknown) {
      // Re-throw with normalized error structure
      const err = error as { response?: { data?: { error?: string; tokensRefunded?: number }; status?: number }; message?: string };
      const errorData = err.response?.data;
      const errorMessage = typeof errorData?.error === 'string'
        ? errorData.error
        : err.message || i18n.t('common:error.videoGeneration');

      const normalizedError = {
        message: errorMessage,
        response: {
          status: err.response?.status || 500,
          data: {
            error: errorMessage,
            tokensRefunded: errorData?.tokensRefunded
          }
        }
      };
      throw normalizedError;
    }
  }

  /**
   * Calculate generation price
   */
  async calculatePrice(params: {
    duration: number;
    fps: FpsOption;
    isPro: boolean;
  }): Promise<{
    tokensRequired: number;
    frameCount: number;
    fps: number;
    duration: number;
    pricePerFrame: number;
    model: string;
  }> {
    const response = await this.client.post('/api/video/restyle/generate/calculate-price', params);
    return response.data.data;
  }

  /**
   * Poll generation status
   * Returns current status and video URL when completed
   */
  async pollStatus(generationId: string): Promise<VideoRestylePollResponse> {
    try {
      const response = await this.client.get('/api/video/restyle/status', {
        params: { generation_id: generationId },
      });
      return response.data.data;
    } catch (error: unknown) {
      // Handle API errors gracefully
      const err = error as { response?: { data?: { error?: string; message?: string } }; message?: string };
      if (err.response?.data) {
        const errorData = err.response.data;
        const errorMessage = typeof errorData.error === 'string'
          ? errorData.error
          : errorData.message || i18n.t('common:error.statusCheck');

        return {
          id: generationId,
          status: 'error',
          error: errorMessage
        };
      }

      return {
        id: generationId,
        status: 'error',
        error: err.message || i18n.t('common:error.network')
      };
    }
  }

  /**
   * Get active video-restyle generations
   */
  async getActiveGenerations(rawTeamId?: string): Promise<{
    success: boolean;
    data?: {
      generations: ActiveRestyleGeneration[];
      count: number;
      isTeamMode?: boolean;
    };
    error?: string;
  }> {
    const teamId = sanitizeTeamId(rawTeamId);
    try {
      const params: Record<string, string> = {};
      if (teamId) params.teamId = teamId;

      const response = await this.client.get('/api/video/restyle/status/active', { params });
      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching active generations:', error);
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      return {
        success: false,
        data: {
          generations: [],
          count: 0,
          isTeamMode: !!teamId
        },
        error: err.response?.data?.error || err.message || i18n.t('common:error.activeGenerationsLoad')
      };
    }
  }

  /**
   * Cancel an active generation
   */
  async cancelGeneration(generationId: string): Promise<{
    success: boolean;
    data?: { id: string; tokensRefunded: number };
    error?: string;
  }> {
    const response = await this.client.post('/api/video/restyle/status/cancel', {
      generation_id: generationId
    });
    return response.data;
  }

  /**
   * Clear stuck generations (NO token refund)
   */
  async clearStuckGenerations(clearAll = false): Promise<{
    success: boolean;
    data?: {
      cleared: number;
      generationIds: string[];
    };
    error?: string;
  }> {
    const response = await this.client.post('/api/video/restyle/status/clear', { clearAll });
    return response.data;
  }

  // ===== HISTORY API =====

  /**
   * Get generation history
   */
  async getHistory(params: {
    teamId?: string;
    offset?: number;
    limit?: number;
  }): Promise<VideoRestyleHistoryResponse> {
    try {
      const sanitizedParams = { ...params, teamId: sanitizeTeamId(params.teamId) };
      const response = await this.client.get('/api/video/restyle/history', { params: sanitizedParams });
      return response.data.data;
    } catch (error: unknown) {
      console.error('Error fetching history:', error);
      return {
        videos: [],
        hasMore: false,
        totalCount: 0
      };
    }
  }

  /**
   * Delete a generation from history
   */
  async deleteGeneration(generationId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/api/video/restyle/${generationId}`);
    return response.data;
  }
}

// Export singleton instance
export const videoRestyleApi = new VideoRestyleApi();
